/**
 * Damage-control visuals for the player ship: hammer sparks and a spreading
 * impact ring where the repair crew is working, plus a smoke/steam plume whose
 * intensity grows as hull health drops.
 *
 * Everything lives in ship-local deck space under the provided `parent` (the
 * ship group), so the effect rides the interpolated hull pose automatically.
 * Both particle systems are fixed pools recycled in place — no per-frame
 * allocation — and all randomness flows through `createSeededRandom`, matching
 * the rest of the project.
 */
import * as THREE from 'three';
import { createSeededRandom } from '../utils/random';

/** Seed for spark/smoke jitter so bot playtests stay reproducible. */
const SEED = 0xd4a4e;

/** Spark particles, recycled in place. */
const SPARK_POOL = 64;
/** Smoke/steam particles, recycled in place. */
const SMOKE_POOL = 48;

/** Sparks emitted per second while a repair hand is actively working. */
const SPARK_EMIT_RATE = 90;
/** Smoke puffs emitted per second at full damage intensity. */
const SMOKE_EMIT_RATE = 16;

const SPARK_LIFE = 0.38;
const SMOKE_LIFE = 2.6;
/** Downward acceleration on flying sparks, m/s². */
const SPARK_GRAVITY = 6;
/** Upward drift of the smoke plume, m/s. */
const SMOKE_RISE = 1.3;

/** Deck height in ship-local space (matches `CrewAvatars.DECK_Y`). */
const DECK_Y = 1.9;

/**
 * Ship-local origin of the smoke plume. This mirrors `REPAIR_ANCHORS` in
 * `server/src/systems/NPCCrew.ts` so steam rises from the damage-control station
 * even when `triggerRepair` has not been called yet.
 */
const SMOKE_ORIGIN_X = -3.5;
const SMOKE_ORIGIN_Z = 0;

/** Seconds for one hammer-ring pulse (~1.8 strikes/s, matching the avatar). */
const RING_PERIOD = 0.56;

/** Converts world-metre point sizes into pixels (`px = size * SCALE / depth`). */
const POINT_SCALE = 800;

interface Particle {
  position: THREE.Vector3;
  velocity: THREE.Vector3;
  age: number;
  life: number;
  size: number;
  alpha: number;
  color: THREE.Color;
}

const VERTEX_SHADER = /* glsl */ `
  attribute float aAlpha;
  attribute float aSize;
  attribute vec3 aColor;

  uniform float uScale;

  varying float vAlpha;
  varying vec3 vColor;

  void main() {
    vAlpha = aAlpha;
    vColor = aColor;

    vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
    float depth = -mvPosition.z;
    gl_PointSize = (aAlpha > 0.0 && depth > 0.0) ? aSize * (uScale / depth) : 0.0;
    gl_Position = projectionMatrix * mvPosition;
  }
`;

const FRAGMENT_SHADER = /* glsl */ `
  varying float vAlpha;
  varying vec3 vColor;

  void main() {
    if (vAlpha <= 0.002) discard;

    vec2 uv = gl_PointCoord - vec2(0.5);
    float disc = smoothstep(0.5, 0.12, length(uv));
    if (disc <= 0.004) discard;

    gl_FragColor = vec4(vColor, vAlpha * disc);
  }
`;

export class DamageFx {
  /** Root group added to the ship; all children are ship-local. */
  readonly group = new THREE.Group();

  private readonly rng = createSeededRandom(SEED);

  private readonly sparks: Particle[] = [];
  private readonly smoke: Particle[] = [];
  private sparkCursor = 0;
  private smokeCursor = 0;
  private sparkAccumulator = 0;
  private smokeAccumulator = 0;

  private readonly sparkPositions: Float32Array;
  private readonly sparkAlphas: Float32Array;
  private readonly sparkSizes: Float32Array;
  private readonly sparkColors: Float32Array;
  private readonly smokePositions: Float32Array;
  private readonly smokeAlphas: Float32Array;
  private readonly smokeSizes: Float32Array;
  private readonly smokeColors: Float32Array;

  private readonly sparkGeometry: THREE.BufferGeometry;
  private readonly sparkMaterial: THREE.ShaderMaterial;
  private readonly sparkPoints: THREE.Points;
  private readonly smokeGeometry: THREE.BufferGeometry;
  private readonly smokeMaterial: THREE.ShaderMaterial;
  private readonly smokePoints: THREE.Points;
  private readonly ringGeometry: THREE.RingGeometry;
  private readonly ringMaterial: THREE.MeshBasicMaterial;
  private readonly ring: THREE.Mesh;

  private active = false;
  private repairX = 0;
  private repairZ = 0;
  private smokeIntensity = 0;
  private time = 0;

  constructor(parent: THREE.Object3D) {
    this.group.name = 'damage-fx';

    this.sparkPositions = new Float32Array(SPARK_POOL * 3);
    this.sparkAlphas = new Float32Array(SPARK_POOL);
    this.sparkSizes = new Float32Array(SPARK_POOL);
    this.sparkColors = new Float32Array(SPARK_POOL * 3);
    this.smokePositions = new Float32Array(SMOKE_POOL * 3);
    this.smokeAlphas = new Float32Array(SMOKE_POOL);
    this.smokeSizes = new Float32Array(SMOKE_POOL);
    this.smokeColors = new Float32Array(SMOKE_POOL * 3);

    this.fillPool(this.sparks, SPARK_POOL);
    this.fillPool(this.smoke, SMOKE_POOL);

    this.sparkGeometry = this.buildGeometry(
      this.sparkPositions,
      this.sparkAlphas,
      this.sparkSizes,
      this.sparkColors,
    );
    this.smokeGeometry = this.buildGeometry(
      this.smokePositions,
      this.smokeAlphas,
      this.smokeSizes,
      this.smokeColors,
    );

    this.sparkMaterial = new THREE.ShaderMaterial({
      uniforms: { uScale: { value: POINT_SCALE } },
      vertexShader: VERTEX_SHADER,
      fragmentShader: FRAGMENT_SHADER,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });
    this.smokeMaterial = new THREE.ShaderMaterial({
      uniforms: { uScale: { value: POINT_SCALE } },
      vertexShader: VERTEX_SHADER,
      fragmentShader: FRAGMENT_SHADER,
      transparent: true,
      depthWrite: false,
      blending: THREE.NormalBlending,
    });

    this.sparkPoints = new THREE.Points(this.sparkGeometry, this.sparkMaterial);
    this.sparkPoints.name = 'damage-fx-sparks';
    this.sparkPoints.frustumCulled = false;
    this.group.add(this.sparkPoints);

    this.smokePoints = new THREE.Points(this.smokeGeometry, this.smokeMaterial);
    this.smokePoints.name = 'damage-fx-smoke';
    this.smokePoints.frustumCulled = false;
    this.group.add(this.smokePoints);

    this.ringGeometry = new THREE.RingGeometry(0.5, 0.72, 32);
    this.ringGeometry.rotateX(-Math.PI / 2);
    this.ringMaterial = new THREE.MeshBasicMaterial({
      color: 0xffc978,
      transparent: true,
      opacity: 0,
      depthWrite: false,
      side: THREE.DoubleSide,
      blending: THREE.AdditiveBlending,
    });
    this.ring = new THREE.Mesh(this.ringGeometry, this.ringMaterial);
    this.ring.name = 'damage-fx-ring';
    this.ring.visible = false;
    this.group.add(this.ring);

    parent.add(this.group);
  }

  /**
   * Toggle the hammer/spark effect at a repair position.
   *
   * @param active True while a repair hand is hammering the hull.
   * @param x      Ship-local deck X of the repair work.
   * @param z      Ship-local deck Z of the repair work.
   */
  triggerRepair(active: boolean, x: number, z: number): void {
    this.active = active;
    if (Number.isFinite(x)) this.repairX = x;
    if (Number.isFinite(z)) this.repairZ = z;
  }

  /**
   * Set hull health as a fraction (0 = destroyed, 1 = full). Smoke/steam
   * intensity rises as the fraction drops.
   */
  setHealthFraction(fraction: number): void {
    const clamped = Number.isFinite(fraction) ? Math.min(1, Math.max(0, fraction)) : 1;
    this.smokeIntensity = 1 - clamped;
  }

  /** Advance sparks, smoke and the hammer ring for one frame. */
  update(delta: number): void {
    const dt = Math.min(Math.max(delta, 0), 0.05);
    this.time += dt;

    this.emitSparks(dt);
    this.emitSmoke(dt);
    this.integrateSparks(dt);
    this.integrateSmoke(dt);
    this.updateRing();

    this.sparkGeometry.attributes.position.needsUpdate = true;
    this.sparkGeometry.attributes.aAlpha.needsUpdate = true;
    this.sparkGeometry.attributes.aSize.needsUpdate = true;
    this.sparkGeometry.attributes.aColor.needsUpdate = true;
    this.smokeGeometry.attributes.position.needsUpdate = true;
    this.smokeGeometry.attributes.aAlpha.needsUpdate = true;
    this.smokeGeometry.attributes.aSize.needsUpdate = true;
    this.smokeGeometry.attributes.aColor.needsUpdate = true;
  }

  dispose(): void {
    this.group.removeFromParent();
    this.sparkGeometry.dispose();
    this.sparkMaterial.dispose();
    this.smokeGeometry.dispose();
    this.smokeMaterial.dispose();
    this.ringGeometry.dispose();
    this.ringMaterial.dispose();
    this.sparks.length = 0;
    this.smoke.length = 0;
  }

  // --- setup helpers -------------------------------------------------------

  private fillPool(pool: Particle[], count: number): void {
    for (let i = 0; i < count; i += 1) {
      pool.push({
        position: new THREE.Vector3(0, -1000, 0),
        velocity: new THREE.Vector3(),
        age: 0,
        life: 0,
        size: 0,
        alpha: 0,
        color: new THREE.Color(),
      });
    }
  }

  private buildGeometry(
    positions: Float32Array,
    alphas: Float32Array,
    sizes: Float32Array,
    colors: Float32Array,
  ): THREE.BufferGeometry {
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', dynamicAttribute(new THREE.BufferAttribute(positions, 3)));
    geometry.setAttribute('aAlpha', dynamicAttribute(new THREE.BufferAttribute(alphas, 1)));
    geometry.setAttribute('aSize', dynamicAttribute(new THREE.BufferAttribute(sizes, 1)));
    geometry.setAttribute('aColor', dynamicAttribute(new THREE.BufferAttribute(colors, 3)));
    geometry.boundingSphere = new THREE.Sphere(new THREE.Vector3(), Infinity);
    return geometry;
  }

  // --- emission ------------------------------------------------------------

  private emitSparks(dt: number): void {
    if (!this.active) return;
    this.sparkAccumulator += SPARK_EMIT_RATE * dt;
    while (this.sparkAccumulator >= 1) {
      this.sparkAccumulator -= 1;
      this.spawnSpark();
    }
  }

  private spawnSpark(): void {
    const particle = this.sparks[this.sparkCursor];
    this.sparkCursor = (this.sparkCursor + 1) % SPARK_POOL;
    const rng = this.rng;
    const angle = rng() * Math.PI * 2;
    const speed = 1.4 + rng() * 2.6;

    particle.position.set(
      this.repairX + (rng() - 0.5) * 0.34,
      DECK_Y + 0.12 + rng() * 0.2,
      this.repairZ + (rng() - 0.5) * 0.34,
    );
    particle.velocity.set(Math.cos(angle) * speed, 1.2 + rng() * 2.2, Math.sin(angle) * speed);
    particle.age = 0;
    particle.life = SPARK_LIFE * (0.6 + rng() * 0.8);
    particle.size = 0.05 + rng() * 0.08;
    particle.alpha = 0.85 + rng() * 0.15;
    particle.color.setHex(rng() < 0.5 ? 0xffc966 : 0xfff2d0);
  }

  private emitSmoke(dt: number): void {
    if (this.smokeIntensity <= 0.01) return;
    this.smokeAccumulator += SMOKE_EMIT_RATE * this.smokeIntensity * dt;
    while (this.smokeAccumulator >= 1) {
      this.smokeAccumulator -= 1;
      this.spawnSmoke();
    }
  }

  private spawnSmoke(): void {
    const particle = this.smoke[this.smokeCursor];
    this.smokeCursor = (this.smokeCursor + 1) % SMOKE_POOL;
    const rng = this.rng;
    const drift = 0.16 + rng() * 0.2;

    particle.position.set(
      SMOKE_ORIGIN_X + (rng() - 0.5) * 0.7,
      DECK_Y + 0.15,
      SMOKE_ORIGIN_Z + (rng() - 0.5) * 0.7,
    );
    particle.velocity.set(
      (rng() - 0.5) * drift,
      SMOKE_RISE + rng() * 0.5,
      (rng() - 0.5) * drift,
    );
    particle.age = 0;
    particle.life = SMOKE_LIFE * (0.7 + rng() * 0.6);
    particle.size = 0.5 + rng() * 0.75;
    particle.alpha = 0.14 + this.smokeIntensity * 0.22;
    particle.color.setHex(0x8f8f96);
  }

  // --- simulation ----------------------------------------------------------

  private integrateSparks(dt: number): void {
    for (let i = 0; i < SPARK_POOL; i += 1) {
      const particle = this.sparks[i];
      const offset = i * 3;
      if (particle.life <= 0 || particle.age >= particle.life) {
        this.sparkAlphas[i] = 0;
        this.sparkSizes[i] = 0;
        continue;
      }

      particle.age += dt;
      particle.velocity.y -= SPARK_GRAVITY * dt;
      particle.position.x += particle.velocity.x * dt;
      particle.position.y += particle.velocity.y * dt;
      particle.position.z += particle.velocity.z * dt;

      const t = Math.min(1, particle.age / particle.life);
      const fade = 1 - t;
      this.sparkPositions[offset] = particle.position.x;
      this.sparkPositions[offset + 1] = particle.position.y;
      this.sparkPositions[offset + 2] = particle.position.z;
      this.sparkAlphas[i] = particle.alpha * fade;
      this.sparkSizes[i] = particle.size * (1 - 0.4 * t);
      this.sparkColors[offset] = particle.color.r;
      this.sparkColors[offset + 1] = particle.color.g;
      this.sparkColors[offset + 2] = particle.color.b;
    }
  }

  private integrateSmoke(dt: number): void {
    for (let i = 0; i < SMOKE_POOL; i += 1) {
      const particle = this.smoke[i];
      const offset = i * 3;
      if (particle.life <= 0 || particle.age >= particle.life) {
        this.smokeAlphas[i] = 0;
        this.smokeSizes[i] = 0;
        continue;
      }

      particle.age += dt;
      particle.position.x += particle.velocity.x * dt;
      particle.position.y += particle.velocity.y * dt;
      particle.position.z += particle.velocity.z * dt;

      const t = Math.min(1, particle.age / particle.life);
      const fade = (1 - t) * (1 - t);
      this.smokePositions[offset] = particle.position.x;
      this.smokePositions[offset + 1] = particle.position.y;
      this.smokePositions[offset + 2] = particle.position.z;
      this.smokeAlphas[i] = particle.alpha * fade;
      this.smokeSizes[i] = particle.size * (0.8 + 1.5 * t);
      this.smokeColors[offset] = particle.color.r;
      this.smokeColors[offset + 1] = particle.color.g;
      this.smokeColors[offset + 2] = particle.color.b;
    }
  }

  private updateRing(): void {
    if (!this.active) {
      this.ring.visible = false;
      return;
    }
    this.ring.visible = true;
    const t = (this.time % RING_PERIOD) / RING_PERIOD;
    this.ring.scale.setScalar(0.4 + t * 2.6);
    this.ringMaterial.opacity = Math.max(0, 1 - t) * 0.85;
    this.ring.position.set(this.repairX, DECK_Y + 0.02, this.repairZ);
  }
}

function dynamicAttribute<T extends THREE.BufferAttribute>(attribute: T): T {
  attribute.setUsage(THREE.DynamicDrawUsage);
  return attribute;
}
